<script>
  import { onMount } from 'svelte';
  // import LoanForce post function
  import postToLoanForce from '../util/postToLoanForce';
  // import reCAPTCHA utility
  import { getRecaptchaToken } from '../util/recaptcha';
  // import and destructure validators and validation functions
  import { validators, validationFunctions } from '../util/validation';
  const {
    alphaNumericValidator,
    emailValidator,
    lengthValidator,
    telValidator,
  } = validators;
  const {
    addErrorsToFields,
    checkIfFieldsHaveErrors,
    clearFieldValues,
    validateFields,
  } = validationFunctions;

  // props provided from WordPress settings & PHP wp_localize_script (in setup.php)
  // passed into the component when it's instantiated in firstTimeHomebuyer.js route script
  export let account;
  export let loanforceId;
  export let campaignPrefix;
  export let sourceName;
  export let company;
  export let hbguideUrl;

  // field data
  let firstName = {
    value: '',
    touched: false,
    error: false,
    errorMessage:
      'Please enter your first name using only letters, spaces, and numbers',
    validator: alphaNumericValidator,
  };
  let lastName = {
    value: '',
    touched: false,
    error: false,
    errorMessage:
      'Please enter your last name using only letters, spaces, and numbers',
    validator: alphaNumericValidator,
  };
  let email = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a valid email address',
    validator: emailValidator,
  };
 let phone = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a valid phone number',
    validator: telValidator,
  };
  let branchLocation = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please select a branch location',
    validator: lengthValidator,
  };

  // do any fields have errors?
  let fieldsHaveErrors = true;

  // to keep track of POST request on form submission
  let isSending = false;
  let isSent = false;
  let isErrorSending = false;

  // height of form for ensuring container is same size when form is sent
  let formHeight;

  // branch locations data
  let branchLocations = [];
  let branchLocationsLoading = true;

  // gets list of branch locations via WordPress AJAX
  async function getBranchLocations() {
    const ajaxUrl = '/wp-admin/admin-ajax.php';
    
    try {
      const response = await fetch(ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'action=get_branch_locations'
      });
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        branchLocations = data.map(branch => ({
          value: branch.title,
          name: branch.title
        }));
      } else {
        // Fallback options if no branches found
        branchLocations = [
          { value: 'Chesapeake, VA', name: 'Chesapeake, VA' },
          { value: 'Gulf Coast', name: 'Gulf Coast' },
          { value: 'Maitland, FL', name: 'Maitland, FL' },
          { value: 'Newport News, VA', name: 'Newport News, VA' },
          { value: 'Outer Banks, NC', name: 'Outer Banks, NC' },
          { value: 'Richmond, VA', name: 'Richmond, VA' },
          { value: 'Virginia Beach, VA', name: 'Virginia Beach, VA' }
        ];
      }
    } catch (error) {
      console.error('Failed to load branch locations:', error);
      
      // Fallback options on error
      branchLocations = [
        { value: 'Chesapeake, VA', name: 'Chesapeake, VA' },
        { value: 'Gulf Coast', name: 'Gulf Coast' },
        { value: 'Maitland, FL', name: 'Maitland, FL' },
        { value: 'Newport News, VA', name: 'Newport News, VA' },
        { value: 'Outer Banks, NC', name: 'Outer Banks, NC' },
        { value: 'Richmond, VA', name: 'Richmond, VA' },
        { value: 'Virginia Beach, VA', name: 'Virginia Beach, VA' }
      ];
    } finally {
      branchLocationsLoading = false;
    }
  }

  onMount(() => {
    // set formHeight to clientHeight of form element
    formHeight = document.getElementById('home-buyers-guide-form').clientHeight;
     // Load branch locations
     getBranchLocations();
  });

  /**
   * Reactive statements to update field data
   * I am still figuring out how to work with these so there maybe room for improvement :) (as of 10/30/2019)
   **/

  // checks if the field is valid on change - don't fire if the form has already been sent
  // without the !isSent conditional it'll geek out and try to validate after POSTing
  $: if (!isSent) {
    // this apparently has to be re-declared in every reactive statement
    const fields = [firstName, lastName, email, phone, branchLocation];

    validateFields(fields);

    // keep track of if any fields have errors, used in submission function to stop submission if there are errors
    fieldsHaveErrors = checkIfFieldsHaveErrors(fields);
  }

  // mask for phone input - adds input mask in (###) ###-#### format
  // from https://stackoverflow.com/questions/17651207/mask-us-phone-number-string-with-javascript
  function addPhoneInputMask() {
    const phoneInput = phone.value
      .replace(/\D/g, '')
      .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    phone.value = !phoneInput[2]
      ? phoneInput[1]
      : '(' +
        phoneInput[1] +
        ') ' +
        phoneInput[2] +
        (phoneInput[3] ? '-' + phoneInput[3] : '');
  }

  // on "Download Now" button click/form submission, add errors to fields that are blank or haven't been touched
  $: if (isSending && fieldsHaveErrors) {
    // if declared outside of reactive statements, they will not update the values
    const fields = [firstName, lastName, email, phone, branchLocation];

    addErrorsToFields(fields);

    isSending = false;
  }

  // clear field values when form is successfully sent
  // tying this reactively to isSent makes using the clearFieldValues function viable, instead of targeting each field indivdually (see older commits for that)
  $: if (isSent) {
    // idk why lol Svelte is weird
    const fields = [firstName, lastName, email, phone, branchLocation];

    clearFieldValues(fields);

    // have to assign isSent to itself to trigger the reactive update?
    isSent = isSent;
  }

  // submit to LoanForce API
  async function submitHbGuideForm() {
    isSending = true;

    // any errors? don't send if so
    // this works in conjunction with the reactive (isSending && fieldsHaveErrors) to stop sending and add errors that might not be visible in the UI
    if (fieldsHaveErrors) {
      return;
    }

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('hb_guide');
      
      // Verify reCAPTCHA token with WordPress
      const verifyResponse = await fetch('/wp-json/sanctum/v1/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: recaptchaToken }),
      });
      
      const verifyResult = await verifyResponse.json();
      
      // If reCAPTCHA verification fails, show error and stop
      if (!verifyResult.success) {
        console.error('reCAPTCHA verification failed:', verifyResult);
        isErrorSending = true;
        isSending = false;
        return;
      }

      const dataToSend = {
        FirstName: firstName.value,
        LastName: lastName.value,
        Email: email.value,
        Phone: phone.value,
        CampaignName: `${campaignPrefix} - HB Guide`,
        CampaignStatus: 'Active',
        Source: `${sourceName} - HB Guide - ${branchLocation.value}`,
        UserAccountName: `${account}`,
        Id: `${loanforceId}`,
        SyncWithMarketo: true,
        EmailOptIn: true,
      };

      const result = await fetch('https://social-media-server-414e76d9eb8b.herokuapp.com/api/websites/create-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });

      const apiResponse = await result.json();

      console.log(apiResponse)
      if (apiResponse.success === true) {
        isErrorSending = false;
        isSent = true;
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
        window.open(hbguideUrl, '_blank');
      } else {
        isErrorSending = true;
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      isErrorSending = true;
      isSending = false;
    }
  }
</script>

{#if isSent}
  <div
    class="flex flex-col justify-center text-center lg:text-left"
    style="height: {formHeight}px">
    <h2 class="text-blue-primary text-22 font-medium mb-8 lg:text-29">
      Thank you for your request!
    </h2>
    <h3 class="text-blue-primary text-18 lg:text-19">
      Click here to download your
      <a
        href={hbguideUrl}
        target="_blank"
        rel="noopener noreferrer"
        class="text-18 text-blue-primary underline hover:text-blue-extradark
        lg:text-19">
        free copy of the Ultimate Guide to Buying Your Dream home!
      </a>
    </h3>
  </div>
{:else}
  <form
    id="home-buyers-guide-form"
    on:submit|preventDefault={submitHbGuideForm}
    novalidate
    class="flex flex-col lg:flex-row">
    <div class="flex flex-col lg:w-1/2">
      <div class="form-field -mb-8">
        <input
          type="text"
          id="firstName-hbguide"
          name="firstName-hbguide"
          bind:value={firstName.value}
          class="form-input"
          required />
        <label for="firstName-hbguide" class="form-label">First Name</label>
        {#if firstName.touched && firstName.error}
          <p class="text-red-error text-14 m-0 leading-none lg:text-15 -mt-4 lg:-mt-8">
            {firstName.errorMessage}
          </p>
        {:else}
          <p class="text-14 m-0 leading-none lg:text-15">&nbsp;</p>
        {/if}
      </div>
      <div class="form-field -mb-8">
        <input
          type="text"
          id="lastName-hbguide"
          name="lastName-hbguide"
          bind:value={lastName.value}
          class="form-input"
          required />
        <label for="lastName-hbguide" class="form-label">Last Name</label>
        {#if lastName.touched && lastName.error}
          <p class="text-red-error text-14 m-0 leading-none lg:text-15 -mt-4 lg:-mt-8">
            {lastName.errorMessage}
          </p>
        {:else}
          <p class="text-14 m-0 leading-none lg:text-15">&nbsp;</p>
        {/if}
      </div>
      <div class="form-field -mb-8">
        <input
          type="text"
          id="email-hbguide"
          name="email-hbguide"
          bind:value={email.value}
          class="form-input"
          required />
        <label for="email-hbguide" class="form-label">Email Address</label>
        {#if email.touched && email.error}
          <p class="text-red-error text-14 m-0 leading-none lg:text-15 -mt-4 lg:-mt-8">
            {email.errorMessage}
          </p>
        {:else}
          <p class="text-14 m-0 leading-none lg:text-15">&nbsp;</p>
        {/if}
      </div>
      <div class="form-field -mb-8">
        <input
          type="text"
          id="phone"
          name="phone"
          bind:value={phone.value}
          on:input={addPhoneInputMask}
          class="form-input"
          required />
        <label for="phone-hbguide" class="form-label">Phone</label>
        {#if phone.touched && phone.error}
          <p class="text-red-error text-14 m-0 leading-none lg:text-15 -mt-4 lg:-mt-8">
            {phone.errorMessage}
          </p>
        {:else}
          <p class="text-14 m-0 leading-none lg:text-15">&nbsp;</p>
        {/if}
      </div>
      <div class="form-field">
        <select
          id="branchLocation-hbguide"
          name="branchLocation-hbguide"
          bind:value={branchLocation.value}
          class="form-input p-2"
          aria-label="Select nearest office location"
          disabled={branchLocationsLoading}
          required>
          {#if branchLocationsLoading}
            <option>Loading locations...</option>
          {:else}
            <option value="" selected disabled>Select nearest office location</option>
            {#each branchLocations as branch}
              <option class="" value={branch.value}>
                {branch.name}
              </option>
            {/each}
          {/if}
        </select>
        {#if branchLocation.touched && branchLocation.error}
          <p class="text-red-error text-14 m-0 leading-none lg:text-15 mt-1">
            {branchLocation.errorMessage}
          </p>
        {:else}
          <p class="text-14 m-0 leading-none lg:text-15">&nbsp;</p>
        {/if}
      </div>
    </div>
    <div class="flex items-center justify-center lg:w-1/2 lg:pl-40">
      {#if !isSending}
        <div class="flex flex-col items-center">
          <div class="btn-outer border-green-button w-108 sm:w-144">
            <input
              type="submit"
              value="Download Now"
              class="btn btn-action bg-green-button w-full" />
          </div>
          <p class="text-12 mt-12">
            By clicking the ‘Download Now’ button above, I authorize {company}
            to contact me using an automated dialing system, even if the
            telephone number is a cellular phone or other service for which the
            party is charged. I understand that I am not required to provide
            this consent in order to obtain goods or services from {company}. I
            agree to receive disclosures and communications in electronic form
            and I confirm that I have a working computer system and an internet
            website browser to view this webpage and this loan inquiry form. I
            agree that {company} may contact me by mail, telephone (mobile
            devices including SMS and MMS) or email in connection with my
            request even if my telephone number or email address appears on a {company}
            internal Do Not Call / Do Not Email List, a State or National Do Not
            Call Registry, a Do Not E-Mail list, or any other Do Not Contact
            List.
          </p>
          {#if isErrorSending}
            <p class="text-red-error text-18 textleading-none mt-6">
              Sorry, there was an error with your submission - please try again.
            </p>
          {/if}
        </div>
      {:else}
        <svg class="spinner h-40 w-40" viewbox="0 0 50 50">
          <circle class="path" cx="25" cy="25" r="20" />
        </svg>
      {/if}
    </div>
  </form>
{/if}



// WEBPACK FOOTER //
// ./scripts/svelte/HbGuideForm.svelte